/**
 * AdminBranding — Página para tenant configurar branding (logo, cores, CSS)
 * White Label Fase 6
 */

import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { Button, Input } from '../components/ui';

interface BrandingData {
  name: string;
  logo_url: string;
  favicon_url: string;
  primary_color: string;
  accent_color: string;
  bg_color: string;
  custom_css: string;
}

const defaults: BrandingData = {
  name: '', logo_url: '', favicon_url: '',
  primary_color: '#6C63FF', accent_color: '#FF6584', bg_color: '#0a0a0a',
  custom_css: '',
};

export function AdminBranding() {
  const [form, setForm] = useState<BrandingData>({ ...defaults });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get<any>('/tenant/branding').then((res: any) => {
      const d = res?.data || res || {};
      setForm({
        name: d.name || '',
        logo_url: d.logo_url || '',
        favicon_url: d.favicon_url || '',
        primary_color: d.primary_color || defaults.primary_color,
        accent_color: d.accent_color || defaults.accent_color,
        bg_color: d.bg_color || defaults.bg_color,
        custom_css: d.custom_css || '',
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true); setMsg(''); setErr('');
    try {
      await api.put('/tenant/branding', form);
      setMsg('✅ Branding salvo com sucesso!');
    } catch (e: any) {
      setErr(e?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const update = (key: keyof BrandingData, val: string) =>
    setForm(prev => ({ ...prev, [key]: val }));

  if (loading) return <div style={{ padding: 32, color: T.textMuted }}>Carregando...</div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 }}>🎨 Branding</h1>
      <p style={{ color: T.textMuted, fontSize: 14, marginBottom: 24 }}>
        Personalize a aparência da sua plataforma
      </p>

      {msg && <div style={{ padding: '10px 14px', background: '#1a3a2a', color: '#4ade80', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{msg}</div>}
      {err && <div style={{ padding: '10px 14px', background: '#3a1a1a', color: '#f87171', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Formulário */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field label="Nome da Marca">
            <Input value={form.name} onInput={(e: any) => update('name', e.target.value)} placeholder="Minha Plataforma" />
          </Field>

          <Field label="Logo URL">
            <Input value={form.logo_url} onInput={(e: any) => update('logo_url', e.target.value)} placeholder="https://..." />
            {form.logo_url && <img src={form.logo_url} alt="Logo" style={{ height: 40, marginTop: 8, objectFit: 'contain', borderRadius: 4 }} onError={(e: any) => e.target.style.display = 'none'} />}
          </Field>

          <Field label="Favicon URL">
            <Input value={form.favicon_url} onInput={(e: any) => update('favicon_url', e.target.value)} placeholder="https://..." />
            {form.favicon_url && <img src={form.favicon_url} alt="Favicon" style={{ height: 24, marginTop: 8, objectFit: 'contain' }} onError={(e: any) => e.target.style.display = 'none'} />}
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <ColorField label="Cor Primária" value={form.primary_color} onChange={(v) => update('primary_color', v)} />
            <ColorField label="Cor de Destaque" value={form.accent_color} onChange={(v) => update('accent_color', v)} />
            <ColorField label="Cor de Fundo" value={form.bg_color} onChange={(v) => update('bg_color', v)} />
          </div>

          <Field label="CSS Customizado">
            <textarea
              value={form.custom_css}
              onInput={(e: any) => update('custom_css', e.target.value)}
              placeholder="/* CSS personalizado */"
              style={{
                width: '100%', minHeight: 100, padding: '10px 12px',
                background: T.inputBg || '#1a1a2e', color: T.text, border: `1px solid ${T.border}`,
                borderRadius: 8, fontFamily: 'monospace', fontSize: 13, resize: 'vertical',
              }}
            />
          </Field>

          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : '💾 Salvar Branding'}
          </Button>
        </div>

        {/* Preview ao vivo */}
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.textMuted, marginBottom: 12 }}>Preview ao Vivo</div>
          <div style={{
            background: form.bg_color || '#0a0a0a',
            borderRadius: 12, padding: 24, border: `1px solid ${T.border}`,
            minHeight: 300,
          }}>
            {/* Header simulado */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              {form.logo_url ? (
                <img src={form.logo_url} alt="" style={{ height: 32, objectFit: 'contain', borderRadius: 4 }}
                     onError={(e: any) => e.target.style.display = 'none'} />
              ) : (
                <div style={{ width: 32, height: 32, borderRadius: 8, background: form.primary_color }} />
              )}
              <span style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{form.name || 'Minha Plataforma'}</span>
            </div>

            {/* Cards simulados */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{
                background: form.primary_color + '22', borderRadius: 8, padding: 16,
                border: `1px solid ${form.primary_color}44`,
              }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: form.primary_color }}>42</div>
                <div style={{ fontSize: 12, color: '#999' }}>Usuários</div>
              </div>
              <div style={{
                background: form.accent_color + '22', borderRadius: 8, padding: 16,
                border: `1px solid ${form.accent_color}44`,
              }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: form.accent_color }}>8</div>
                <div style={{ fontSize: 12, color: '#999' }}>Agentes</div>
              </div>
            </div>

            {/* Botão simulado */}
            <div style={{ marginTop: 16 }}>
              <div style={{
                display: 'inline-block', padding: '8px 20px', borderRadius: 8,
                background: form.primary_color, color: '#fff', fontSize: 13, fontWeight: 600,
              }}>
                Botão Primário
              </div>
              <div style={{
                display: 'inline-block', padding: '8px 20px', borderRadius: 8, marginLeft: 8,
                background: form.accent_color, color: '#fff', fontSize: 13, fontWeight: 600,
              }}>
                Botão Destaque
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <div>
      <label style={{ fontSize: 13, fontWeight: 600, color: T.textMuted, marginBottom: 4, display: 'block' }}>{label}</label>
      {children}
    </div>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, marginBottom: 4, display: 'block' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="color"
          value={value}
          onInput={(e: any) => onChange(e.target.value)}
          style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer', background: 'none' }}
        />
        <input
          type="text"
          value={value}
          onInput={(e: any) => onChange(e.target.value)}
          style={{
            flex: 1, padding: '6px 8px', fontSize: 12, fontFamily: 'monospace',
            background: T.inputBg || '#1a1a2e', color: T.text, border: `1px solid ${T.border}`,
            borderRadius: 6,
          }}
        />
      </div>
    </div>
  );
}
